import { Motion } from '@capacitor/motion';
import { Capacitor } from '@capacitor/core';
import type { Vec2 } from '../types';

export type MotionPermission = 'unknown' | 'granted' | 'denied' | 'unavailable';

/**
 * Tilt input from device orientation sensors.
 * Uses Capacitor Motion plugin for native iOS/Android,
 * falls back to DeviceOrientationEvent for web.
 */
export class TiltInput {
  /** Current smoothed tilt */
  readonly tilt: Vec2 = { x: 0, y: 0 };
  /** Raw sensor values (for debugging) */
  readonly rawTilt: Vec2 = { x: 0, y: 0 };
  /** Calibration offset */
  readonly calibrationOffset: Vec2 = { x: 0, y: 0 };

  permission: MotionPermission = 'unknown';

  /** Smoothing factor (0 = no smoothing, 1 = frozen) */
  smoothing = 0.3;
  /** Deadzone radius (smaller = more responsive to micro-tilts) */
  deadzone = 0.008; // Was 0.015
  /** Sensitivity multiplier */
  sensitivity = 1.0;
  /** Max tilt in degrees that maps to full input */
  maxTiltDegrees = 80;
  /** Response curve exponent (<1 = amplifies small movements) */
  responseExponent = 0.85; // Was 1.0
  /** Invert X axis */
  invertX = false;
  /** Invert Y axis */
  invertY = false;

  private isNative: boolean;
  private webHandler: ((e: DeviceOrientationEvent) => void) | null = null;
  private orientationHandle: { remove: () => void } | null = null;

  constructor() {
    this.isNative = Capacitor.isNativePlatform();

    // Check if device orientation is available
    if (!this.isNative && typeof DeviceOrientationEvent === 'undefined') {
      this.permission = 'unavailable';
    }
  }

  /**
   * Request motion permission.
   * In native Capacitor, this is handled automatically.
   * In web Safari, triggers the permission dialog.
   */
  async requestPermission(): Promise<MotionPermission> {
    if (this.permission === 'granted' || this.permission === 'unavailable') {
      return this.permission;
    }

    if (this.isNative) {
      // Capacitor handles permissions natively
      try {
        await this.startCapacitorListening();
        this.permission = 'granted';
        return this.permission;
      } catch (e) {
        console.error('Capacitor motion error:', e);
        this.permission = 'denied';
        return this.permission;
      }
    }

    // Web fallback - check for iOS Safari permission API
    const DOE = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };

    if (typeof DOE.requestPermission === 'function') {
      try {
        const result = await DOE.requestPermission();
        this.permission = result;
        if (result === 'granted') {
          this.startWebListening();
        }
        return this.permission;
      } catch {
        this.permission = 'denied';
        return this.permission;
      }
    }

    // No permission needed (Android Chrome, older iOS, desktop)
    this.permission = 'granted';
    this.startWebListening();
    return this.permission;
  }

  /** Start listening via Capacitor Motion plugin */
  private async startCapacitorListening(): Promise<void> {
    this.orientationHandle = await Motion.addListener('orientation', (event) => {
      this.handleOrientation(event.beta ?? 0, event.gamma ?? 0);
    });
  }

  /** Start listening via web DeviceOrientationEvent */
  private startWebListening(): void {
    if (this.webHandler) return;

    this.webHandler = (e: DeviceOrientationEvent) => {
      this.handleOrientation(e.beta ?? 0, e.gamma ?? 0);
    };
    window.addEventListener('deviceorientation', this.webHandler);
  }

  /** Handle orientation values from either source */
  private handleOrientation(beta: number, gamma: number): void {
    // Normalize to roughly [-1, 1] range
    // ~25 degrees = full tilt, no hard-coded offset (use calibration)
    const rawX = gamma / this.maxTiltDegrees;
    const rawY = beta / this.maxTiltDegrees;

    this.rawTilt.x = rawX;
    this.rawTilt.y = rawY;

    // Apply calibration offset
    let x = rawX - this.calibrationOffset.x;
    let y = rawY - this.calibrationOffset.y;

    // Apply sensitivity
    x *= this.sensitivity;
    y *= this.sensitivity;

    // Apply inversion
    if (this.invertX) x = -x;
    if (this.invertY) y = -y;

    // Apply deadzone
    const mag = Math.sqrt(x * x + y * y);
    if (mag < this.deadzone) {
      x = 0;
      y = 0;
    } else {
      // Rescale to remove deadzone gap
      const scale = (mag - this.deadzone) / (1 - this.deadzone) / mag;
      x *= scale;
      y *= scale;
    }

    // Clamp to [-1, 1]
    x = Math.max(-1, Math.min(1, x));
    y = Math.max(-1, Math.min(1, y));

    // Response curve for better small-tilt control
    x = this.applyResponseCurve(x);
    y = this.applyResponseCurve(y);

    // Exponential moving average smoothing
    this.tilt.x = this.tilt.x * this.smoothing + x * (1 - this.smoothing);
    this.tilt.y = this.tilt.y * this.smoothing + y * (1 - this.smoothing);
  }

  private applyResponseCurve(value: number): number {
    const abs = Math.abs(value);
    if (abs === 0) return 0;
    const curved = Math.pow(abs, this.responseExponent);
    return Math.sign(value) * curved;
  }

  /** Calibrate: set current orientation as "neutral" */
  calibrate(): void {
    this.calibrationOffset.x = this.rawTilt.x;
    this.calibrationOffset.y = this.rawTilt.y;
    this.tilt.x = 0;
    this.tilt.y = 0;
  }

  /** Set calibration from saved data */
  setCalibration(offset: Vec2): void {
    this.calibrationOffset.x = offset.x;
    this.calibrationOffset.y = offset.y;
  }

  /** Dispose of event listeners */
  dispose(): void {
    if (this.orientationHandle) {
      this.orientationHandle.remove();
      this.orientationHandle = null;
    }
    if (this.webHandler) {
      window.removeEventListener('deviceorientation', this.webHandler);
      this.webHandler = null;
    }
  }
}
